import type { BarcodeProduct } from '../../api/barcodeScans';
import type { ScanTargetResult } from '../../store/scanTargetStore';
import {
  DraftLine, displayUnit, ensureTrailingEmpty, isEmptyLine, isVolumeUnit, newLine,
} from './types';

export interface BarcodeLinesResult extends ScanTargetResult {
  lines: DraftLine[];
  bulkProductId?: string;
}

export const addBarcodeProductToLines = (
  current: DraftLine[],
  product: BarcodeProduct,
): BarcodeLinesResult => {
  if (product.requires_data_matrix) {
    return {
      ok: false,
      lines: current,
      message: `«${product.name}» — штучный маркированный товар. Отсканируйте Data Matrix`,
    };
  }

  const lines = current.filter(l => !isEmptyLine(l));
  const unit = displayUnit(product.unit_name) || 'шт';
  const price = product.price != null ? Number(product.price) : 0;

  if (isVolumeUnit(unit)) {
    const existing = lines.find(l => l.external_1c_id === product.id && isVolumeUnit(l.unit));
    if (existing) {
      return {
        ok: false,
        duplicate: true,
        lines: current,
        message: `«${product.name}» уже добавлен — укажите объём в существующей строке`,
      };
    }
    const line: DraftLine = {
      ...newLine(),
      external_1c_id: product.id,
      name: product.name,
      quantity: 0,
      unit,
      price,
      recommended_price: price,
      price_from_price_list: product.price_from_price_list || undefined,
      is_bulk_master: true,
      children: [],
      available_marks: null,
      marking_code: null,
    };
    return {
      ok: true,
      key: line.key,
      lines: ensureTrailingEmpty([...lines, line]),
      bulkProductId: product.id,
      message: `«${product.name}» добавлен — укажите объём`,
    };
  }

  const markedMaster = lines.find(l => l.external_1c_id === product.id && l.is_piece_master);
  if (markedMaster) {
    return {
      ok: false,
      lines: current,
      message: `«${product.name}» уже добавлен по маркам. Для нового экземпляра отсканируйте Data Matrix`,
    };
  }

  const existing = lines.find(l =>
    l.external_1c_id === product.id
    && !l.parent_key
    && !l.is_piece_master
    && !l.is_bulk_master
    && !l.marking_code
    && !isVolumeUnit(l.unit)
  );
  if (existing) {
    const next = lines.map(l => l.key === existing.key ? { ...l, quantity: l.quantity + 1 } : l);
    return {
      ok: true,
      key: existing.key,
      lines: ensureTrailingEmpty(next),
      message: `«${product.name}»: количество ${existing.quantity + 1}`,
    };
  }

  const line: DraftLine = {
    ...newLine(),
    external_1c_id: product.id,
    name: product.name,
    quantity: 1,
    unit,
    price,
    recommended_price: price,
    price_from_price_list: product.price_from_price_list || undefined,
    marking_code: null,
  };
  return {
    ok: true,
    key: line.key,
    lines: ensureTrailingEmpty([...lines, line]),
    message: `«${product.name}» добавлен`,
  };
};
